import React, { useEffect, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdDeleteForever, MdEdit, MdPerson } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Form } from "@unform/web";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import DeleteOperatorModal from "./DeleteOperatorModal";
import Input from "../../components/Form/Input";
import messageTypes from "../../constants/MessagesTypes";
import { getOperators, deleteOperator } from "../../services/Operator";
import { getCompany } from "../../services/Company";
import {
  OperatorWrapper,
  PageHeader,
  NewOperatorButton,
  OperatorCard,
  FilterSection,
  FilterTitle,
  FormWrapper,
  StyledFilterFormWrapper,
  ContentArea,
  StyledTableContainer,
  ActionButtons,
  PaginationInfo,
  PaginationButton,
  LoadingButton,
  EmptyState,
  TableWrapper,
  FilterRow,
  FilterInputWrapper,
  FilterButtonWrapper
} from "./styles";

export default function OperatorList() {
  const [loading, setLoading] = useState(false);
  const [deleteOperatorId, setDeleteOperatorId] = useState(null);
  const [total, setTotal] = useState(0);
  const [operators, setOperators] = useState([]);
  const [company, setCompany] = useState(null);
  const [page, setPage] = useState(1);
  const { companyId } = useParams();
  const { isAdmin } = useSelector((state) => state.CompanyReducer || { isAdmin: false });

  const formRef = useRef(null);

  const [filter, setFilter] = useState({
    stringSearch: '',
  });

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem acessar esta página.');
      return;
    }

    if (!companyId) {
      toast.error('ID da empresa é obrigatório.');
      return;
    }

    // Carregar dados da empresa
    getCompany(companyId).then((data) => {
      if (data && data.data && data.data.data) {
        setCompany(data.data.data);
      }
    });

    setLoading(true);

    getOperators(companyId, {
      page: page,
      per_page: 25,
      stringSearch: filter.stringSearch,
    }).then((data) => {
      if (!data || data === '') {
        setOperators([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      if (data.data && data.data.data) {
        setOperators(data.data.data);
        setTotal(data.data.data.length);
      } else {
        setOperators([]);
        setTotal(0);
      }
      setLoading(false);
    }).catch(() => {
      setOperators([]);
      setTotal(0);
      setLoading(false);
    });

    return function cleanup() {
      setOperators([]);
    };
  }, [page, deleteOperatorId, filter, companyId, isAdmin]);

  async function handleSubmit(data) {
    try {
      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setFilter({
        stringSearch: data.stringSearch,
      });
      setPage(1);

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
      }
    }
  }

  function handleDelete(id) {
    setDeleteOperatorId(id);
  }

  async function handleConfirmDelete() {
    try {
      await deleteOperator(deleteOperatorId);
      toast.success(messageTypes.SUCCESS_DELETE || 'Funcionário excluído com sucesso!');
      setDeleteOperatorId(null);
      // Recarregar a lista
      setLoading(true);
      getOperators(companyId, {
        page: page,
        per_page: 25,
        stringSearch: filter.stringSearch,
      }).then((data) => {
        if (!data || data === '') {
          setOperators([]);
          setTotal(0);
          setLoading(false);
          return;
        }

        if (data.data && data.data.data) {
          setOperators(data.data.data);
          setTotal(data.data.data.length);
        } else {
          setOperators([]);
          setTotal(0);
        }
        setLoading(false);
        // Ajustar página se necessário
        if (operators.length === 1 && page > 1) {
          setPage(page - 1);
        }
      }).catch(() => {
        setOperators([]);
        setTotal(0);
        setLoading(false);
      });
    } catch (error) {
      toast.error(error.response?.data?.message || messageTypes.ERROR_DELETE || 'Erro ao excluir funcionário');
      setDeleteOperatorId(null);
    }
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <OperatorWrapper>
      <DivDiagonal />
      <ContentArea>
        <PageHeader>
          <div>
            <h1>
              <i className="fas fa-users"></i>
              Funcionários
            </h1>
            {company && (
              <p style={{ color: '#b0b0b0', marginTop: '8px', fontSize: '14px' }}>
                Empresa: <strong style={{ color: '#f29205' }}>{company.name}</strong>
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/company" style={{ textDecoration: 'none' }}>
              <CustomButton
                variant="outlined"
                className="btn btn-secondary font-weight-bold text-capitalize"
                type="button"
                startIcon={<i className="fas fa-arrow-left" />}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#e0e0e0',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Voltar
              </CustomButton>
            </Link>
            {companyId && (
              <NewOperatorButton as={Link} to={`/operator/${companyId}/new`}>
                <i className="fas fa-plus"></i>
                Novo Funcionário
              </NewOperatorButton>
            )}
          </div>
        </PageHeader>

        <OperatorCard>
          <FilterSection>
            <StyledFilterFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <FilterTitle>
                  <i className="fas fa-funnel-dollar"/> Filtro
                </FilterTitle>
                <FilterRow>
                  <FilterInputWrapper>
                    <Input
                      type="text"
                      name="stringSearch"
                      placeholder="Pesquisar funcionário"
                      label={'Pesquisar funcionário'}
                    />
                  </FilterInputWrapper>
                  <FilterButtonWrapper>
                    <CustomButton
                      className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                      type="submit"
                      startIcon={<AiOutlineSearch/>}
                      style={{
                        background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0 32px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                        transition: 'all 0.3s ease',
                        width: 'auto',
                        minWidth: 'auto',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                      }}
                    >
                      Buscar
                    </CustomButton>
                  </FilterButtonWrapper>
                </FilterRow>
              </Form>
            </StyledFilterFormWrapper>
          </FilterSection>
          <TableWrapper>
            {loading ? (
              <LoadingButton
                className="btn btn-primary text-capitalize font-weight-bold"
                loading={loading}
                type="button"
              >
                Carregando...
              </LoadingButton>
            ) : (
              <>
                {operators && operators.length ? (
                  <StyledTableContainer>
                    <Table sx={{minWidth: 650}} aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell align="center">Email</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Opções</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {operators.map((operator) => (
                          <TableRow key={operator.id}>
                            <TableCell>
                              <strong style={{ color: '#ffffff' }}>{operator.name}</strong>
                            </TableCell>
                            <TableCell align="center">
                              <span style={{ color: '#b0b0b0' }}>{operator.email}</span>
                            </TableCell>
                            <TableCell align="center">
                              <span
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  background: (operator.active !== undefined && operator.active !== false)
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : 'rgba(244, 67, 54, 0.2)',
                                  color: (operator.active !== undefined && operator.active !== false) ? '#4caf50' : '#f44336',
                                }}
                              >
                                {(operator.active !== undefined && operator.active !== false) ? 'Ativo' : 'Inativo'}
                              </span>
                            </TableCell>
                            <TableCell align="center">
                              <ActionButtons>
                                <Link to={`/operator/${companyId}/${operator.id}/edit`}>
                                  <MdEdit />
                                </Link>
                                <button
                                  onClick={() => handleDelete(operator.id)}
                                  type="button"
                                >
                                  <MdDeleteForever />
                                </button>
                              </ActionButtons>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      {total > 0 && (
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={4}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '16px',
                                }}
                              >
                                <PaginationInfo>
                                  Mostrando {operators.length} de {total} funcionários
                                </PaginationInfo>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <PaginationButton
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                  >
                                    <IoIosArrowBack />
                                  </PaginationButton>
                                  <PaginationButton
                                    onClick={() => setPage(page + 1)}
                                    disabled={operators.length < 25}
                                  >
                                    <IoIosArrowForward />
                                  </PaginationButton>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </StyledTableContainer>
                ) : (
                  <EmptyState>
                    <MdPerson />
                    <p>Nenhum funcionário encontrado</p>
                    <span>Cadastre um novo funcionário para começar</span>
                  </EmptyState>
                )}
              </>
            )}
          </TableWrapper>
        </OperatorCard>
      </ContentArea>

      <DeleteOperatorModal
        isOpen={!!deleteOperatorId}
        onClose={() => setDeleteOperatorId(null)}
        onConfirm={handleConfirmDelete}
        operatorId={deleteOperatorId}
      />
    </OperatorWrapper>
  );
}

